import type { AxiosResponse } from 'axios';

/** Triggers a browser "save as" for an in-memory blob. */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Extracts the filename from a `Content-Disposition` header, if present. */
export function filenameFromContentDisposition(
  header: string | undefined,
  fallback: string,
): string {
  if (!header) return fallback;
  // RFC 5987: filename*=UTF-8''encoded-name
  const extended = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].replace(/"/g, '').trim());
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() ?? fallback;
}

/**
 * Saves an Axios blob response as a file, honoring the server's
 * `Content-Disposition` filename (falling back to `fallbackFilename`). Used by
 * every authenticated file download — the plain `<a href>` approach cannot
 * send the bearer token the API requires.
 */
export function saveBlobResponse(
  response: AxiosResponse<Blob>,
  fallbackFilename: string,
): void {
  const filename = filenameFromContentDisposition(
    response.headers['content-disposition'] as string | undefined,
    fallbackFilename,
  );
  triggerBlobDownload(response.data, filename);
}
